import common from '../common';
import LevelImporter from '../LevelImporter';
import Player from '../physics/Player';
import Grid from './grid';
import THREEParticleEmitter from '../particles/THREEParticleEmitter';

export default class world {
	constructor() {
		this.stage = common.THREEstage;

		this.player = null;
		this.goal = null;
		this.grid = null;
		this.walls = [];
		this.levelimporter = null;
		this.cameraPath = null;
		this.crates = [];

		this._traps = [];
		this._resetables = [];
		this._tilesToUpdate = [];

		this._raycaster = null;
		this._raycastFrame = 0;
		this._hiddenObjects = [];
		this._cameraPosition = new THREE.Vector3();
		this._cameraDirection = new THREE.Vector3();

		this._dataParticles = new THREE.Group();

		this.mapHeight = 0;
		this.mapWidth = 0;

		this.init();
	}

	init() {
		/* Ambient Light*/
		const alight = new THREE.AmbientLight(0xFFFFFF, 2);
		this.stage.add(alight);

		const eLight = new THREE.HemisphereLight(0xFFFFFF, 0xFFFFFF, 3);
		this.stage.add(eLight);

		this.grid = new Grid(32, 32);
		common.grid = this.grid;

		this.levelimporter = new LevelImporter(this.grid);
		this.levelimporter.signals.loadcomplete.once(this.onLevelLoad, this);
		this.levelimporter.load(common.levelSettings.filename);

		this._raycaster = new THREE.Raycaster();
		this._raycaster.near = 0;
		this._raycaster.far = 10;
	}

	onLevelLoad(ctx, room, width, height) {
		this.stage.add(room);

		common.config.debug_map && this.grid.mapGrid();

		this._tilesToUpdate = room.tilesToUpdate;

		this.mapWidth = width;
		this.mapHeight = height;
		this.walls = room.walls;
		this.cameraPath = room.cameraPath;
		this.crates = room.crates;

		this.goal = room.goal;
		this._traps = room.traps;
		this._resetables = room.resetables;

		this.player = new Player(this, room.player.tile);
		room.player.tile.addObject(this.player);
		const pos = room.player.tile.position3D;

		this.player.mesh.position.set(pos.x, pos.y + 0.4, pos.z);

		const particles = new THREEParticleEmitter(P3.assets.json('particle_3d_data_emitter_00'), true);
		this._dataParticles.add(particles);
		common.particles3D.push(particles);

		const particles2 = new THREEParticleEmitter(P3.assets.json('particle_3d_data_emitter_01'), true);
		this._dataParticles.add(particles2);
		common.particles3D.push(particles2);

		this.stage.add(this._dataParticles);

		this._dataParticles.play = () => {
			particles.play();
			particles2.play();
		};

		// quick hack. we play the particles for 2 seconds off-screen so the textures are uploaded to the GPU and not display as squares.
		setTimeout(() => {
			particles.stop();
			particles2.stop();
		}, 2000);
	}

	raycast() {
		common.camera.getWorldPosition(this._cameraPosition);
		common.camera.getWorldDirection(this._cameraDirection);
		this._raycaster.set(this._cameraPosition, this._cameraDirection);

		return this._raycaster.intersectObjects(common.world.crates, true);
	}

	hideObjects() {
		if (++this._raycastFrame % 8 !== 0) {
			return;
		}	// only raycast every 16 frames for performance
		// first cycle through all objects, and unhide them
		// then raycast and find new objects between camera and player
		// hide them

		for (const object of this._hiddenObjects) {
			object.material.transparent = false;
			object.material.opacity = 1;
		}
		this._hiddenObjects.length = 0;

		const intersects = this.raycast();

		if (intersects.length > 0) {
			const object = intersects[0].object;

			object.material.transparent = true;
			object.material.opacity = 0.2;
			this._hiddenObjects.push(object);
		}
	}

	getBounds() {
		const bounds =
        {
        	xmin : 1000,
        	xmax : -1000,
        	zmin : 1000,
        	zmax : -1000
        };

		const {width, height} = this.grid._tileDimensions;

		for (let i = 0; i < this.grid._tiles.length; i++) {
			const tile = this.grid._tiles[i];

			if (!tile.isTile) {
				continue;
			}

			if (tile.x * width < bounds.xmin) {
				bounds.xmin = tile.x * width;
			}

			if ((tile.x * width) + width > bounds.xmax) {
				bounds.xmax = (tile.x * width) + width;
			}

			if ((tile.y * height) - height < bounds.zmin) {
				bounds.zmin = (tile.y * height) - height;
			}

			if ((tile.y * height) > bounds.zmax) {
				bounds.zmax = tile.y * height;
			}
		}

		return bounds;
	}

	getCoins() {
		let counter = 0;

		for (let i = 0; i < this.grid._tiles.length; i++) {
			const tile = this.grid._tiles[i];

			if (tile.types.includes('gem')) {
				counter++;
			}
		}

		return counter;
	}

	resetTraps() {
		for (const trap of this._traps) {
			trap.activate && trap.activate(true);
		}

		for (const entity of this._resetables) {
			entity.reset();
		}
	}

	destroy() {
		this.stage.traverse(e => {
			if (e.isMesh) {
				e.geometry.dispose();
				e.material.dispose();
			} else if (e.dispose) {
				e.dispose();
			}
		});

		this.player.destroy();

		for (const tile of this.grid.tiles) {
			tile.destroy();
		}

		this.levelimporter.destroy();

		while (this.stage.children.length > 0) {
			this.stage.remove(this.stage.children[0]);
		}
	}

	update() {
		this.player && this.player.update();

		for (const tile of this._tilesToUpdate) {
			tile.update();
		}
	}

	get dataParticles() {
		return this._dataParticles;
	}

	set dataParticles(v) {
		this._dataParticles = v;
	}
}
